import { IPlanPoint } from '../shared-plan-geometry/plan-geometry';

/** A vertex of a flat part outline in mm; `bulge` bends the edge to the next vertex into an arc (DXF convention). */
export interface IPartVertex {
    x: number;
    y: number;
    bulge: number;
}

/** Outline lengths and angles are compared to this precision when parts are matched (mm, rad). */
const LENGTH_STEP: number = 0.1;
const ANGLE_STEP: number = 0.001;

/** A plan outline seen from above as a flat part: X stays, Z (down on the plan) becomes −Y. */
export function planToPart(points: readonly IPlanPoint[], bulges: readonly number[] = []): IPartVertex[] {
    // The flip mirrors the outline, so an arc turns the other way.
    return points.map((point: IPlanPoint, index: number) => ({ x: point.x, y: -point.z, bulge: -(bulges[index] ?? 0) }));
}

function signedArea(outline: readonly IPartVertex[]): number {
    let area: number = 0;
    for (let i: number = 0; i < outline.length; i++) {
        const a: IPartVertex = outline[i];
        const b: IPartVertex = outline[(i + 1) % outline.length];
        area += a.x * b.y - b.x * a.y;
    }
    return area / 2;
}

/** The same outline walked the other way: each bulge moves to the edge's new start and changes sign. */
function reversed(outline: readonly IPartVertex[]): IPartVertex[] {
    const count: number = outline.length;
    return outline.map((_vertex: IPartVertex, index: number) => {
        const vertex: IPartVertex = outline[count - 1 - index];
        const edgeStart: IPartVertex = outline[(2 * count - 2 - index) % count];
        return { x: vertex.x, y: vertex.y, bulge: -edgeStart.bulge };
    });
}

/** Moves an outline so the lower-left corner of its bounding box lies at (0, 0), counter-clockwise. */
export function normalizeOutline(outline: readonly IPartVertex[]): IPartVertex[] {
    const minX: number = Math.min(...outline.map((vertex: IPartVertex) => vertex.x));
    const minY: number = Math.min(...outline.map((vertex: IPartVertex) => vertex.y));
    const moved: IPartVertex[] = outline.map((vertex: IPartVertex) => ({ ...vertex, x: vertex.x - minX, y: vertex.y - minY }));
    return signedArea(moved) >= 0 ? moved : reversed(moved);
}

/** Width and height of an outline's bounding box (straight edges; an arc's bow is not added). */
export function outlineSize(outline: readonly IPartVertex[]): { width: number; height: number } {
    const xs: number[] = outline.map((vertex: IPartVertex) => vertex.x);
    const ys: number[] = outline.map((vertex: IPartVertex) => vertex.y);
    return { width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
}

/** Each edge as a rigid-motion invariant: its length, the turn at its end and its bulge, rounded. */
function edgeTokens(outline: readonly IPartVertex[]): string[] {
    const count: number = outline.length;
    return outline.map((vertex: IPartVertex, index: number) => {
        const next: IPartVertex = outline[(index + 1) % count];
        const after: IPartVertex = outline[(index + 2) % count];
        const inX: number = next.x - vertex.x;
        const inY: number = next.y - vertex.y;
        const outX: number = after.x - next.x;
        const outY: number = after.y - next.y;
        const turn: number = Math.atan2(inX * outY - inY * outX, inX * outX + inY * outY);
        return [Math.round(Math.hypot(inX, inY) / LENGTH_STEP), Math.round(turn / ANGLE_STEP), Math.round(vertex.bulge / ANGLE_STEP)].join(
            ':'
        );
    });
}

/** The smallest of all cyclic shifts of a token list, joined: the same for any start vertex. */
function minimalRotation(tokens: readonly string[]): string {
    let best: string | null = null;
    for (let shift: number = 0; shift < tokens.length; shift++) {
        const candidate: string = [...tokens.slice(shift), ...tokens.slice(0, shift)].join('|');
        if (best === null || candidate.localeCompare(best) < 0) {
            best = candidate;
        }
    }
    return best ?? '';
}

/**
 * A key equal for two outlines that coincide after a move, a turn or a flip — a board cut the same
 * way, whichever face is up. Lengths match to 0.1 mm.
 */
export function outlineKey(outline: readonly IPartVertex[]): string {
    const ccw: IPartVertex[] = normalizeOutline(outline);
    const mirrored: IPartVertex[] = normalizeOutline(
        ccw.map((vertex: IPartVertex) => ({ x: -vertex.x, y: vertex.y, bulge: -vertex.bulge }))
    );
    const own: string = minimalRotation(edgeTokens(ccw));
    const flipped: string = minimalRotation(edgeTokens(mirrored));
    return own.localeCompare(flipped) < 0 ? own : flipped;
}
